import React, { createContext, useContext, ReactNode, useMemo } from "react";

import { useUserData } from "hooks";

import { EddPermissions, KycPermissions, PromotionRequestPermissions, RemediationPermissions } from "./types";
import {
  getEddPermissions,
  getKycPermissions,
  getPromotionRequestPermissions,
  getRemediationPermissions,
} from "./utils";

export type Permissions = EddPermissions & PromotionRequestPermissions & KycPermissions & RemediationPermissions;

type PermissionsContextType = {
  isLoading: boolean;
  permissions: Permissions;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useUserData();

  const eddPermissions = useMemo(() => getEddPermissions(data), [data]);
  const promotionRequestPermissions = useMemo(() => getPromotionRequestPermissions(data), [data]);
  const kycPermissions = useMemo(() => getKycPermissions(data), [data]);
  const remediationPermissions = useMemo(() => getRemediationPermissions(data), [data]);

  const permissions: Permissions = {
    ...eddPermissions,
    ...promotionRequestPermissions,
    ...kycPermissions,
    ...remediationPermissions,
  };

  return <PermissionsContext.Provider value={{ isLoading, permissions }}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};
